import { useCallback, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import Main from 'app/frontend/base/components/Main'
import Select from 'app/frontend/base/components/Select'
import { Button } from '@/components/ui/button'
import {
  DevAssignmentEncounter,
  DevAssignmentInstance,
  DevRaidPlanAssignmentDefaults,
  getDevRaidPlanAssignmentDefaults,
  getDevRaidPlanAssignmentInstances,
  saveDevRaidPlanAssignmentDefaults,
} from 'app/frontend/base/api/devRaidPlanAssignmentDefaults'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import RaidPlan, { RaidPlanTimeline } from 'app/frontend/types/custom/RaidPlan'
import useTeamPlanComputedData from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/hooks/useTeamPlanComputedData'
import TeamPlanAssignments from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/TeamPlanAssignments'
import { TeamPlanTimelineProvider } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/TeamPlanTimelineContext'

const DEV_OWNER: RaidPlanOwner = { ownerType: 'user' }

const cloneTimeline = (timeline: RaidPlanTimeline): RaidPlanTimeline =>
  JSON.parse(JSON.stringify(timeline)) as RaidPlanTimeline

const assignmentsFromTimeline = (
  timeline: RaidPlanTimeline,
): DevRaidPlanAssignmentDefaults => ({
  abilities: timeline.encounter_assignments?.abilities ?? {},
  ability_order: timeline.encounter_assignments?.ability_order ?? [],
})

const timelineFromAssignments = (
  assignments: DevRaidPlanAssignmentDefaults,
): RaidPlanTimeline => ({
  actors: [],
  encounter_assignments: {
    abilities: assignments.abilities ?? {},
    ability_order: assignments.ability_order ?? [],
  },
})

const buildFakeRaidPlan = (
  encounterId: number,
  timeline: RaidPlanTimeline,
): RaidPlan => ({
  id: 0,
  ownerId: 0,
  ownerType: 'User',
  encounterId,
  difficulty: 'mythic',
  name: 'Assignment defaults',
  timeline,
  createdAt: '',
  updatedAt: '',
})

const RaidPlanAssignmentDefaultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const instanceId = searchParams.get('instance') ?? undefined
  const encounterId = searchParams.get('encounter') ?? undefined

  const { data, isLoading, error } = useQuery({
    queryKey: ['devRaidPlanAssignmentInstances'],
    queryFn: getDevRaidPlanAssignmentInstances,
  })

  const instances = data?.instances ?? []

  const selectedInstance = useMemo(
    () => instances.find((instance) => String(instance.id) === instanceId),
    [instances, instanceId],
  )

  const encounters = selectedInstance?.encounters ?? []

  const selectedEncounter = useMemo(
    () => encounters.find((encounter) => String(encounter.id) === encounterId),
    [encounters, encounterId],
  )

  const instanceOptions = instances.map((instance: DevAssignmentInstance) => ({
    value: String(instance.id),
    label: `${instance.name} (${instance.gameVersion})`,
    sortLabel: '',
  }))

  const encounterOptions = encounters.map(
    (encounter: DevAssignmentEncounter) => ({
      value: String(encounter.id),
      label: encounter.name,
      sortLabel: encounter.name,
    }),
  )

  const updateParams = (next: { instance?: string; encounter?: string }) => {
    const params = new URLSearchParams()
    if (next.instance) params.set('instance', next.instance)
    if (next.encounter) params.set('encounter', next.encounter)
    setSearchParams(params, { replace: true })
  }

  return (
    <Main title='Raid plan assignment defaults'>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-wrap items-end gap-4'>
          <div className='flex flex-col gap-2 min-w-[240px]'>
            <div className='text-sm text-muted-foreground'>Instance</div>
            <Select
              value={instanceId}
              setValue={(value) => {
                const instance = instances.find(
                  (entry) => String(entry.id) === value,
                )
                updateParams({
                  instance: value,
                  encounter: instance?.encounters[0]
                    ? String(instance.encounters[0].id)
                    : undefined,
                })
              }}
              options={instanceOptions}
              isSearchable
              placeholder='Select instance'
            />
          </div>
          <div className='flex flex-col gap-2 min-w-[240px]'>
            <div className='text-sm text-muted-foreground'>Encounter</div>
            <Select
              value={encounterId}
              setValue={(value) => {
                updateParams({
                  instance: instanceId,
                  encounter: value,
                })
              }}
              options={encounterOptions}
              disabled={!selectedInstance}
              placeholder='Select encounter'
            />
          </div>
        </div>

        {isLoading ? (
          <div className='text-muted-foreground'>Loading…</div>
        ) : null}

        {error ? (
          <div className='text-destructive'>
            Failed to load assignment defaults. This page is development-only.
          </div>
        ) : null}

        {selectedEncounter ? (
          <AssignmentDefaultsWorkspace
            key={selectedEncounter.id}
            encounterId={selectedEncounter.id}
          />
        ) : null}
      </div>
    </Main>
  )
}

const AssignmentDefaultsWorkspace = ({
  encounterId,
}: {
  encounterId: number
}) => {
  const { data: assignments, isLoading } = useQuery({
    queryKey: ['devRaidPlanAssignmentDefaults', encounterId],
    queryFn: () => getDevRaidPlanAssignmentDefaults(encounterId),
  })

  if (isLoading || !assignments) {
    return <div className='text-muted-foreground'>Loading…</div>
  }

  return (
    <AssignmentDefaultsEditor
      encounterId={encounterId}
      initialAssignments={assignments}
    />
  )
}

const AssignmentDefaultsEditor = ({
  encounterId,
  initialAssignments,
}: {
  encounterId: number
  initialAssignments: DevRaidPlanAssignmentDefaults
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const timelineRootRef = useRef<HTMLDivElement>(null)
  const [, setAssignmentDialogOpen] = useState(false)
  const [timeline, setTimeline] = useState<RaidPlanTimeline>(() =>
    timelineFromAssignments(initialAssignments),
  )
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify(
      assignmentsFromTimeline(timelineFromAssignments(initialAssignments)),
    ),
  )

  const updateTimeline = useCallback(
    (updater: (next: RaidPlanTimeline) => void) => {
      setTimeline((current) => {
        const next = cloneTimeline(current)
        updater(next)
        return next
      })
    },
    [],
  )

  const saveMutation = useMutation({
    mutationFn: async () => {
      return await saveDevRaidPlanAssignmentDefaults(
        encounterId,
        assignmentsFromTimeline(timeline),
      )
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(
        ['devRaidPlanAssignmentDefaults', encounterId],
        saved,
      )
      const next = timelineFromAssignments(saved)
      setTimeline(next)
      setSavedSnapshot(JSON.stringify(assignmentsFromTimeline(next)))
      toast.success('Saved assignment defaults')
    },
    onError: (saveError) => {
      toast.error(
        saveError instanceof Error
          ? saveError.message
          : 'Failed to save assignment defaults',
      )
    },
  })

  const raidPlan = useMemo(
    () => buildFakeRaidPlan(encounterId, timeline),
    [encounterId, timeline],
  )

  const computed = useTeamPlanComputedData({
    raidPlan,
    timeline,
    selectedType: 'assignments',
    t,
  })

  const isDirty =
    JSON.stringify(assignmentsFromTimeline(timeline)) !== savedSnapshot

  const timelineContextValue = useMemo(
    () => ({
      canEdit: true,
      owner: DEV_OWNER,
      basePath: '/dev/raid_plan_assignment_defaults',
      planListPathPrefix: '/dev/raid_plan_assignment_defaults',
      rosterMemberships: [],
      raidPlan,
      timeline,
      isInitializing: false,
      selectedType: 'assignments' as const,
      setSelectedType: () => undefined,
      zoom: null,
      setZoom: () => undefined,
      computed,
      timelineRootRef,
      viewers: [],
      cursors: [],
      showRosterImport: false,
      cursorContext: 'tab:assignments',
      setAssignmentDialogOpen,
      sendCursor: () => undefined,
      updateTimeline,
    }),
    [computed, raidPlan, timeline, updateTimeline],
  )

  return (
    <div className='flex flex-col gap-4'>
      <div>
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!isDirty || saveMutation.isPending}
          loading={saveMutation.isPending}
        >
          Save
        </Button>
      </div>
      <div ref={timelineRootRef}>
        <TeamPlanTimelineProvider value={timelineContextValue}>
          <TeamPlanAssignments />
        </TeamPlanTimelineProvider>
      </div>
    </div>
  )
}

export default RaidPlanAssignmentDefaultsPage
